var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import ADD_PRODUCT_VARIANT from 'api/mutations/ADD_PRODUCT_VARIANT';
import ADD_VARIANTS from 'api/mutations/ADD_VARIANTS';
import ASSIGN_PRODUCT_TO_TENANT from 'api/mutations/ASSIGN_PRODUCT_TO_TENANT';
import CONSTRUCT_VARIANTS from 'api/mutations/CONSTRUCT_VARIANTS';
import REMOVE_PRODUCT_VARIANT from 'api/mutations/REMOVE_PRODUCT_VARIANT';
import UPDATE_PRODUCT_VARIANT from 'api/mutations/UPDATE_PRODUCT_VARIANT';
import AUTOCOMPLETE_PRODUCT_LABELS from 'api/queries/AUTOCOMPLETE_PRODUCT_LABELS';
import GET_PRODUCT from 'api/queries/GET_PRODUCT';
import GET_PRODUCT_VARIANTS from 'api/queries/GET_PRODUCT_VARIANTS';
import { parseProduct } from 'parsers/specification/parseProduct';
import client from '../api/environment';
import { decreaseVariantsCounter, increaseVariantsCounter } from './componentsSlice';
import { addToast } from './toastsSlice';
const initialState = {
    item: null,
    status: {
        fetch: 'idle',
        addShared: 'idle',
    },
    error: null,
    variants: {
        items: [],
        status: {
            fetch: 'idle',
            construct: 'idle',
            add: 'idle',
            update: 'idle',
            remove: 'idle',
        },
        error: null,
    },
};
export const fetchComponentDetails = createAsyncThunk('componentDetails/fetchComponentDetails', ({ componentId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const componentLabelsResponse = yield client.query({
            query: AUTOCOMPLETE_PRODUCT_LABELS,
        });
        const componentLabels = componentLabelsResponse.data.autocomplete.productLabels;
        const response = yield client.query({
            query: GET_PRODUCT,
            variables: { productId: componentId },
            fetchPolicy: 'network-only',
        });
        return parseProduct(response.data.node, i18n, componentLabels);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchComponentVariants = createAsyncThunk('components/fetchComponentVariants', (productId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_PRODUCT_VARIANTS,
            variables: { productId },
            fetchPolicy: 'network-only',
        });
        return response.data.node.variants.edges.map((edge) => edge.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const constructVariants = createAsyncThunk('componentDetails/constructVariants', ({ productId, colors, sizes, chainColors, tapeColors, sliderColors, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.mutate({
            mutation: CONSTRUCT_VARIANTS,
            variables: {
                productId,
                colorsStr: colors,
                sizesStr: sizes,
                chainColorsStr: chainColors,
                tapeColorsStr: tapeColors,
                sliderColorsStr: sliderColors,
            },
        });
        return response.data.constructVariantsToAdd;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addVariants = createAsyncThunk('componentDetails/addVariants', ({ productId, variants, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.mutate({
            mutation: ADD_VARIANTS,
            variables: {
                productId,
                variants,
            },
        });
        return response.data;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addSharedComponent = createAsyncThunk('componentDetails/addSharedComponent', ({ componentId, tenantId, variantsPrices, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.mutate({
            mutation: ASSIGN_PRODUCT_TO_TENANT,
            variables: { productId: componentId, tenantId, variantsPrices },
        });
        if (response.data.assignProductToTenant.product) {
            return response;
        }
        return rejectWithValue(response.data.removeProduct.message);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addVariantAsync = createAsyncThunk('componentDetails/addVariant', (variables, { rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    try {
        const response = yield client.mutate({
            mutation: ADD_PRODUCT_VARIANT,
            variables,
        });
        dispatch(increaseVariantsCounter({ id: variables.productId }));
        return response.data.addProductVariant.productVariant;
    }
    catch (error) {
        const apolloError = error;
        if (((_b = (_a = apolloError.graphQLErrors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.message) === 'DataValidationException') {
            dispatch(addToast({
                color: 'negative',
                title: i18n.t(`exceptions:${(_c = apolloError.graphQLErrors[0].extensions) === null || _c === void 0 ? void 0 : _c.exceptionCode}`),
            }));
        }
        return rejectWithValue(apolloError.message);
    }
}));
export const removeVariantAsync = createAsyncThunk('componentDetails/removeVariant', ({ productVariantId, componentId }, { rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _d, _e;
    try {
        yield client.mutate({
            mutation: REMOVE_PRODUCT_VARIANT,
            variables: { productVariantId },
        });
        dispatch(decreaseVariantsCounter({ id: componentId }));
        return { productVariantId };
    }
    catch (error) {
        const apolloError = error;
        if (((_e = (_d = apolloError.graphQLErrors) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.message) === 'ProductAssignedException') {
            dispatch(addToast({
                color: 'negative',
                title: i18n.t('exceptions:product_assigned_cannot_remove_variant'),
            }));
        }
        return rejectWithValue(apolloError.message);
    }
}));
export const updateVariantAsync = createAsyncThunk('componentDetails/updateVariant', (variables, { rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h;
    try {
        const response = yield client.mutate({
            mutation: UPDATE_PRODUCT_VARIANT,
            variables,
        });
        return response.data.updateProductVariant.productVariant;
    }
    catch (error) {
        const apolloError = error;
        if (((_g = (_f = apolloError.graphQLErrors) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.message) === 'DataValidationException') {
            dispatch(addToast({
                color: 'negative',
                title: i18n.t(`exceptions:${(_h = apolloError.graphQLErrors[0].extensions) === null || _h === void 0 ? void 0 : _h.exceptionCode}`),
            }));
        }
        return rejectWithValue(apolloError.message);
    }
}));
const selectedComponentSlice = createSlice({
    name: 'componentDetails',
    initialState,
    reducers: {
        resetComponent(state) {
            state.item = null;
            state.variants = {
                items: [],
                status: {
                    fetch: 'idle',
                    construct: 'idle',
                    add: 'idle',
                },
                error: null,
            };
            state.status = {
                fetch: 'idle',
                addShared: 'idle',
            };
            state.error = null;
        },
        resetVariants(state) {
            state.variants = {
                items: [],
                status: {
                    fetch: 'idle',
                    construct: 'idle',
                    add: 'idle',
                    update: 'idle',
                },
                error: null,
            };
        },
        replacePictures(state, action) {
            if (state.item) {
                state.item.pictures = action.payload;
            }
        },
        removePicture(state, action) {
            if (state.item) {
                state.item.pictures = state.item.pictures.filter((picture) => picture.id !== action.payload);
            }
        },
        updateItem: (state, action) => {
            const _a = action.payload.data, { pictures: _pictures } = _a, data = __rest(_a, ["pictures"]);
            state.item = Object.assign(Object.assign({}, state.item), data);
        },
        addVariant: (state, action) => {
            var _a;
            const { data } = action.payload;
            (_a = state.variants.items) === null || _a === void 0 ? void 0 : _a.push(data.addProductVariant.productVariant);
            if (state.item) {
                state.item.activeProductVariantsCount = (state.item.activeProductVariantsCount || 0) + 1;
            }
        },
        updateVariant: (state, action) => {
            var _a;
            const { id, updatedVariant } = action.payload;
            const itemIndex = (_a = state.variants.items) === null || _a === void 0 ? void 0 : _a.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.variants.items[itemIndex] = Object.assign(Object.assign({}, state.variants.items[itemIndex]), updatedVariant.data.updateProductVariant.productVariant);
            }
        },
        removeVariant: (state, action) => {
            const { id } = action.payload;
            state.variants.items = state.variants.items
                ? state.variants.items.filter((item) => item.id !== id)
                : state.variants.items;
            if (state.item) {
                state.item.activeProductVariantsCount = Math.max((state.item.activeProductVariantsCount || 0) - 1, 0);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchComponentDetails.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchComponentDetails.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.item = action.payload;
        })
            .addCase(fetchComponentDetails.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.error = action.payload || 'Failed to fetch component';
        })
            .addCase(fetchComponentVariants.pending, (state) => {
            state.variants.status.fetch = 'loading';
        })
            .addCase(fetchComponentVariants.fulfilled, (state, action) => {
            state.variants.status.fetch = 'succeeded';
            state.variants.items = action.payload;
        })
            .addCase(fetchComponentVariants.rejected, (state, action) => {
            state.variants.status.fetch = 'failed';
            state.variants.error = action.payload || 'Failed to fetch component';
        })
            .addCase(constructVariants.pending, (state) => {
            state.variants.status.construct = 'loading';
        })
            .addCase(constructVariants.fulfilled, (state) => {
            state.variants.status.construct = 'succeeded';
        })
            .addCase(constructVariants.rejected, (state, action) => {
            state.variants.status.construct = 'failed';
            state.variants.error = action.payload || 'Failed to fetch component';
        })
            .addCase(addVariants.pending, (state) => {
            state.variants.status.add = 'loading';
        })
            .addCase(addVariants.fulfilled, (state) => {
            state.variants.status.add = 'succeeded';
            state.variants.status.fetch = 'idle';
        })
            .addCase(addVariants.rejected, (state, action) => {
            state.variants.status.add = 'failed';
            state.variants.error = action.payload || 'Failed to fetch component';
        })
            .addCase(addSharedComponent.pending, (state) => {
            state.status.addShared = 'loading';
        })
            .addCase(addSharedComponent.fulfilled, (state) => {
            state.status.addShared = 'succeeded';
        })
            .addCase(addSharedComponent.rejected, (state, action) => {
            state.status.addShared = 'failed';
            state.error = action.payload || 'Failed to fetch component';
        })
            .addCase(addVariantAsync.pending, (state) => {
            state.variants.status.add = 'loading';
        })
            .addCase(addVariantAsync.fulfilled, (state, action) => {
            state.variants.status.add = 'succeeded';
            state.variants.items.push(action.payload);
            if (state.item) {
                state.item.activeProductVariantsCount = (state.item.activeProductVariantsCount || 0) + 1;
            }
        })
            .addCase(addVariantAsync.rejected, (state) => {
            state.variants.status.add = 'failed';
        })
            .addCase(updateVariantAsync.pending, (state) => {
            state.variants.status.update = 'loading';
        })
            .addCase(updateVariantAsync.fulfilled, (state, action) => {
            state.variants.status.update = 'succeeded';
            const updated = action.payload;
            const itemIndex = state.variants.items.findIndex((item) => item.id === updated.id);
            if (itemIndex !== -1) {
                state.variants.items[itemIndex] = Object.assign(Object.assign({}, state.variants.items[itemIndex]), updated);
            }
        })
            .addCase(updateVariantAsync.rejected, (state) => {
            state.variants.status.update = 'failed';
        })
            .addCase(removeVariantAsync.pending, (state) => {
            state.variants.status.remove = 'loading';
        })
            .addCase(removeVariantAsync.fulfilled, (state, action) => {
            state.variants.status.remove = 'succeeded';
            const { productVariantId } = action.payload;
            state.variants.items = state.variants.items.filter((item) => item.id !== productVariantId);
            if (state.item) {
                state.item.activeProductVariantsCount = Math.max((state.item.activeProductVariantsCount || 0) - 1, 0);
            }
        })
            .addCase(removeVariantAsync.rejected, (state) => {
            state.variants.status.remove = 'failed';
        });
    },
});
export const { resetComponent, resetVariants, addVariant, updateVariant, removeVariant, replacePictures, removePicture, updateItem, } = selectedComponentSlice.actions;
export default selectedComponentSlice.reducer;
export const selectComponentDetails = (state) => state.componentDetails.item;
export const selectComponentDetailsStatus = (state) => state.componentDetails.status;
export const selectComponentVariants = (state) => state.componentDetails.variants.items;
export const selectVariantsStatus = (state) => state.componentDetails.variants.status;
export const selectVariantById = (state, variantId) => state.variants.items.find((variant) => variant.id === variantId);
